import { useMemo } from 'react';

/**
 * Single source of truth for scene/script permission checks, replacing the
 * hand-rolled `isReadOnly = script.level === 'viewer'` duplicated across
 * sceneTable.js, shotTable.js, Shot.js, and ExpandedForm.js.
 *
 * Plain function (not a hook) so it's safe to call per-item inside a
 * `.map()` loop, e.g. rendering a scene list — calling a hook there would
 * violate the Rules of Hooks. Components that render a single scene should
 * use the `useScenePermissions` hook below instead, for memoization.
 *
 * @param {object} scene - the scene in question (needs scene_position)
 * @param {object} script - the script from ScriptContext (needs level, features, freemium_hard_paywall)
 * @returns {{isReadOnly: boolean, isSceneLocked: boolean, isGenerationLocked: boolean, canEditScene: boolean, canExportScene: boolean}}
 */
export function computeScenePermissions(scene, script) {
  const isReadOnly = script?.level === 'viewer';
  const isPro = (script?.features || '').toLowerCase() === 'pro';
  const isGenerationLocked = !isPro && !!script?.freemium_hard_paywall;
  // Eligibility must be decided by freemium_hard_paywall (set eagerly, at
  // upload/creation time) rather than freemium_slot (assigned lazily, only
  // after a scene's AI generation succeeds). Scenes generate concurrently
  // and finish in whatever order the AI calls happen to return, so gating
  // on freemium_slot made the 5-scene edit lock depend on generation
  // timing instead of scene_position.
  const isFreemiumEligible = !isPro && !isGenerationLocked;
  const position = scene?.scene_position ?? null;
  const isSceneLocked = isFreemiumEligible && position != null && position > 5;

  return {
    isReadOnly,
    isSceneLocked,
    isGenerationLocked,
    canEditScene: !isReadOnly && !isSceneLocked && !isGenerationLocked,
    canExportScene: !isSceneLocked,
  };
}

/**
 * Whether a scene can be dragged from one list index to another. Free-plan
 * scripts only unlock scenes 1-5, and that lock is positional, so a move that
 * crosses that boundary would swap which scenes are unlocked -- those moves
 * are blocked (the server enforces the same rule). Moves within 1-5, or
 * within 6+, are fine.
 *
 * @param {object} script - the script from ScriptContext
 * @param {number} fromIndex - 0-based index the scene is dragged from
 * @param {number} toIndex - 0-based index it's dropped at
 * @returns {boolean}
 */
export function canMoveScene(script, fromIndex, toIndex) {
  if (script?.level === 'viewer') return false;
  const isPro = (script?.features || '').toLowerCase() === 'pro';
  if (isPro) return true;
  if (script?.freemium_hard_paywall) return false;
  return fromIndex < 5 === toIndex < 5;
}

/**
 * Memoized hook wrapper around computeScenePermissions, for components that
 * render a single scene (not inside a loop).
 */
export default function useScenePermissions(scene, script) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(
    () => computeScenePermissions(scene, script),
    [
      scene?.scene_position,
      script?.level,
      script?.features,
      script?.freemium_hard_paywall,
    ],
  );
}
